import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'

import styles from './EnterpriseDomains.module.scss'

import { isApiError } from '@/api'
import { EnterpriseDomainItem } from '@/api/generated'
import { updateEnterpriseGates } from '@/api/internal'
import Button from '@/components/Button'
import { Switch } from '@/components/Switch'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { enterpriseDetailsQueryKey, useEnterpriseDetails } from '@/hooks/useEnterpriseDetails'
import AddEnterpriseDomainDialog from '@/pages/app/Enterprises/components/EnterpriseDomains/AddEnterpriseDomainDialog'
import SyncWorkosDialog from '@/pages/app/Enterprises/components/EnterpriseDomains/SyncWorkosDialog'
import ToggleDomainDeletedDialog from '@/pages/app/Enterprises/components/EnterpriseDomains/ToggleDomainVisibilityDialog'
import { selectIsInternalSuperAdmin } from '@/store/features/user/userSlice'
import { EnterpriseGates } from '@/types'

interface EnterpriseDomainsProps {
  enterpriseId: string
  enterpriseName: string
}

const columnHelper = createColumnHelper<EnterpriseDomainItem>()

const EnterpriseDomains = ({ enterpriseId, enterpriseName }: EnterpriseDomainsProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false)
  const [showSyncWarning, setShowSyncWarning] = useState(false)
  const [pendingToggle, setPendingToggle] = useState<{ domainId: string; domain: string; deleted: boolean } | null>(
    null
  )
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const isSuperAdmin = useSelector(selectIsInternalSuperAdmin)

  const { data: enterpriseDetailsResponse, isLoading, error } = useEnterpriseDetails(enterpriseId)
  const domains = enterpriseDetailsResponse?.data.domains ?? []
  const restrictDomainAccess = enterpriseDetailsResponse?.data.restrict_domain_access ?? false
  const hasWorkosOrg = Boolean(enterpriseDetailsResponse?.data.workos_org_id)

  const restrictMutation = useMutation({
    mutationFn: (gates: Partial<EnterpriseGates>) => updateEnterpriseGates(enterpriseId, gates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enterpriseDetailsQueryKey(enterpriseId) })
    },
    onError: (err) => {
      const detail = isApiError(err) ? err.detail : undefined
      updateToast(detail || 'Failed to update enterprise gates', 'error')
    },
  })

  useEffect(() => {
    if (error) {
      console.error('Failed to fetch enterprise details:', error)
      if (isApiError(error)) {
        updateToast(error.detail || 'Failed to load domains', 'error')
      } else {
        updateToast('Failed to load domains', 'error')
      }
    }
  }, [error, updateToast])

  const handleToggleClick = (domainId: string, domain: string, deleted: boolean) => {
    setPendingToggle({ domainId, domain, deleted })
  }

  const handleToggleSuccess = (workos_synced: boolean) => {
    setPendingToggle(null)
    if (!workos_synced && hasWorkosOrg) {
      setShowSyncWarning(true)
    }
  }

  const handleSyncSuccess = (workos_synced: boolean) => {
    setIsSyncDialogOpen(false)
    if (workos_synced) {
      setShowSyncWarning(false)
    }
  }

  const columns = [
    columnHelper.accessor('domain', {
      header: 'Domain',
      cell: (info) => <span className={styles.domainName}>{info.getValue()}</span>,
    }),
    columnHelper.accessor('created_at', {
      header: 'Created',
      cell: (info) => <span className={styles.domainCreatedAt}>{new Date(info.getValue()).toLocaleDateString()}</span>,
    }),
    columnHelper.accessor('deleted', {
      header: 'Active',
      cell: (info) => {
        const domain = info.row.original
        return (
          <Switch
            checked={!info.getValue()}
            onInputChange={(active) => handleToggleClick(domain.id, domain.domain, !active)}
            size="small"
          />
        )
      },
    }),
  ]

  const table = useReactTable({
    data: domains,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleAddDomainSuccess = (workos_synced: boolean) => {
    setIsDialogOpen(false)
    queryClient.invalidateQueries({ queryKey: enterpriseDetailsQueryKey(enterpriseId) })
    if (!workos_synced && hasWorkosOrg) {
      setShowSyncWarning(true)
    }
  }

  return (
    <div className={styles.domainsSection}>
      <div className={styles.header}>
        <span className={styles.label}>Domains</span>
        <div className={styles.headerActions}>
          <Tooltip text="Updates WorkOS with the enterprise's domains" placement="top" hideArrow>
            <Button title="Sync WorkOS Domains" onClick={() => setIsSyncDialogOpen(true)} type="light" />
          </Tooltip>
          <Button title="+ New" onClick={() => setIsDialogOpen(true)} type="dark" />
        </div>
      </div>

      <div className={styles.restrictRow}>
        <span className={styles.restrictKey}>
          Restrict Domain Access
          <span className={styles.restrictHint}>
            Disallow users from using Wispr if they are not on the enterprise plan
          </span>
        </span>
        <span className={styles.restrictValue}>
          <Tooltip text={!isSuperAdmin ? 'Only superadmins can update this' : undefined} placement="top" hideArrow>
            <Switch
              checked={restrictDomainAccess}
              onInputChange={(checked) => restrictMutation.mutate({ restrictDomainAccess: checked })}
              disabled={!isSuperAdmin || restrictMutation.isPending || isLoading}
            />
          </Tooltip>
        </span>
      </div>

      {showSyncWarning && (
        <div className={styles.syncWarning}>
          <span>
            Domain change applied, but failed to sync to WorkOS. SSO may not reflect the change until manually synced.
          </span>
          <Button title="x" type="link" onClick={() => setShowSyncWarning(false)} className={styles.dismissButton} />
        </div>
      )}

      {isLoading ? (
        <div className={styles.loading}>Loading domains...</div>
      ) : domains.length === 0 ? (
        <div className={styles.empty}>No domains configured for this enterprise</div>
      ) : (
        <table className={styles.domainsTable}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AddEnterpriseDomainDialog
        isOpen={isDialogOpen}
        enterpriseId={enterpriseId}
        enterpriseName={enterpriseName}
        onSave={handleAddDomainSuccess}
        onCancel={() => setIsDialogOpen(false)}
      />

      <ToggleDomainDeletedDialog
        isOpen={pendingToggle !== null}
        enterpriseId={enterpriseId}
        domainId={pendingToggle?.domainId ?? ''}
        domain={pendingToggle?.domain ?? ''}
        deleted={pendingToggle?.deleted ?? false}
        onSuccess={handleToggleSuccess}
        onCancel={() => setPendingToggle(null)}
      />

      <SyncWorkosDialog
        isOpen={isSyncDialogOpen}
        enterpriseId={enterpriseId}
        enterpriseName={enterpriseName}
        onSuccess={handleSyncSuccess}
        onCancel={() => setIsSyncDialogOpen(false)}
      />
    </div>
  )
}

export default EnterpriseDomains
